import { useMemo, useState } from 'react'
import { useNotification } from '@web/walter-ui-kit'
import { useAppSettings, userApi } from '~api'
import { useAuthGuard } from '~hooks/useAuthGuard'
import { getErrorNotification } from '~hooks/usePromise/utils'

const DEFAULT_USER_APP_SETTINGS = { favorites: { currencies: [] } }

enum FavoriteState {
    Add = 'add',
    Delete = 'delete',
}

// eslint-disable-next-line max-lines-per-function
export const useFavorites = () => {
    const { data, mutate } = useAppSettings()
    const { push } = useNotification()
    const [pendingFavoriteList, setPendingFavoriteList] = useState<
        { currency: string; state: FavoriteState.Add | FavoriteState.Delete }[]
    >([])

    const favoriteList = useMemo(
        () => new Set<string>(data?.favorites?.currencies || []),
        [data?.favorites?.currencies]
    )

    const addPending = (currency: string, state: FavoriteState.Add | FavoriteState.Delete) => {
        setPendingFavoriteList(list => {
            const isPending = list.find(item => item.currency === currency)
            if (isPending) {
                return list
            }
            return [...list, { currency, state }]
        })
    }

    const deletePending = (currency: string) => {
        setPendingFavoriteList(list => {
            const isPending = list.find(item => item.currency === currency)
            if (isPending) {
                return list.filter(item => item.currency !== currency)
            }
            return list
        })
    }

    const sendDelete = async (currency: string) => {
        try {
            addPending(currency, FavoriteState.Delete)
            const result = await userApi.deleteUserAppSettings({ currency })
            deletePending(currency)
            return result
        } catch (err) {
            deletePending(currency)
            push(getErrorNotification(err))
            return
        }
    }

    const sendAdd = async (currency: string) => {
        try {
            addPending(currency, FavoriteState.Add)
            const result = await userApi.postUserAppSettings({ currency })
            deletePending(currency)
            return result
        } catch (err) {
            deletePending(currency)
            push(getErrorNotification(err))
            return
        }
    }

    const onFavoriteChange = useAuthGuard(async (currency: string) => {
        const isPending = pendingFavoriteList.find(item => item.currency === currency)

        if (isPending) {
            return
        }

        if (favoriteList.has(currency)) {
            mutate(sendDelete(currency), {
                optimisticData: () => {
                    if (data?.favorites?.currencies) {
                        const nextCurrencies = data.favorites.currencies.filter(item => item !== currency)
                        const currencies = [
                            ...nextCurrencies.filter(dataCurrency => {
                                const pendingItem = pendingFavoriteList.find(item => item.currency === dataCurrency)
                                const isDeleting = pendingItem && pendingItem.state === FavoriteState.Delete

                                return !isDeleting
                            }),

                            ...pendingFavoriteList
                                .filter(item => item.state === FavoriteState.Add)
                                .map(item => item.currency),
                        ]

                        return {
                            ...data,
                            favorites: {
                                ...data.favorites,
                                currencies,
                            },
                        }
                    }
                    return DEFAULT_USER_APP_SETTINGS
                },
                revalidate: false,
            })
        } else {
            mutate(sendAdd(currency), {
                optimisticData: () => {
                    if (data?.favorites?.currencies) {
                        const nextCurrencies = [
                            ...data.favorites.currencies.filter(dataCurrency => {
                                const pendingItem = pendingFavoriteList.find(item => item.currency === dataCurrency)
                                const isDeleting = pendingItem && pendingItem.state === FavoriteState.Delete

                                return !isDeleting
                            }),
                            currency,
                            ...pendingFavoriteList
                                .filter(item => item.state === FavoriteState.Add)
                                .map(item => item.currency),
                        ]

                        return {
                            ...data,
                            favorites: {
                                ...data.favorites,
                                currencies: nextCurrencies,
                            },
                        }
                    }
                    return DEFAULT_USER_APP_SETTINGS
                },
                revalidate: true,
            })
        }
    })

    return { favoriteList, pendingFavoriteList: pendingFavoriteList.map(item => item.currency), onFavoriteChange }
}
